import { IWebhookHeader } from '../../models/Webhooks';

// Pure header-row logic for the webhook editor, kept free of React and the store so it can be unit
// tested without booting the app.
//
// The rows are local state shaped like the API's header plus two fields that never leave the browser:
//   key          a stable React key, so removing a row does not hand its focus/state to the next one
//   originalName the name the header was loaded with, which is how we know whether a saved secret
//                can still be matched by the server (it matches by name)
// On a row, hasValue is narrowed to "the server holds a secret value for this header". The API also
// sets it for plain headers that have a value, but those arrive with the value itself, so there is
// nothing stored to keep or replace.
// toHeaderPayload strips all three before anything is sent.

export const MAX_HEADERS = 25;
export const MAX_HEADER_NAME_LENGTH = 100;
export const MAX_HEADER_VALUE_LENGTH = 4000;

// RFC 7230 token characters.
const TOKEN_NAME = /^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/;

// Mirrors the server's reserved list. Compared lower-cased.
const RESERVED_NAMES = [
    'host', 'content-length', 'transfer-encoding', 'connection', 'keep-alive', 'upgrade', 'expect',
    'te', 'trailer', 'proxy-authorization', 'proxy-connection',
];

// Names a browser refuses to let script set on fetch. Only relevant to the in-browser test request;
// the server sends these fine.
const BROWSER_FORBIDDEN_NAMES = [
    'accept-charset', 'accept-encoding', 'access-control-request-headers', 'access-control-request-method',
    'cookie', 'cookie2', 'date', 'dnt', 'origin', 'referer', 'user-agent', 'via',
    ...RESERVED_NAMES,
];

export interface IHeaderRow extends IWebhookHeader {
    key: string;
    originalName?: string;
}

let rowCounter = 0;
const newKey = (): string => `hdr-${++rowCounter}`;

export const newHeaderRow = (): IHeaderRow => ({ key: newKey(), name: '', value: '', secret: false });

export const toHeaderRows = (headers?: IWebhookHeader[]): IHeaderRow[] =>
    (headers ?? []).map((h) => ({
        key: newKey(),
        name: h.name ?? '',
        value: h.value ?? '',
        secret: !!h.secret,
        hasValue: !!h.secret && !!h.hasValue,
        originalName: h.name ?? '',
    }));

/**
 * The array to send on save. Always the full list: omitting it would leave stored headers alone, so an
 * empty list is how the user removes the last one. A secret with no typed value is sent with an empty
 * value, which is the server's cue to keep the stored secret of that name.
 */
export const toHeaderPayload = (rows: IHeaderRow[]): IWebhookHeader[] =>
    rows.map((r) => ({
        name: (r.name ?? '').trim(),
        value: r.value ?? '',
        secret: !!r.secret,
    }));

/**
 * One error string (or undefined) per row, in row order. Pure so it can be tested without rendering,
 * and so the panel can gate Save on it.
 */
export const validateHeaderRows = (rows: IHeaderRow[]): (string | undefined)[] => {
    const seen = new Set<string>();
    return rows.map((r) => {
        const name = (r.name ?? '').trim();
        const lower = name.toLowerCase();
        const value = r.value ?? '';

        let error: string | undefined;
        if (!name) {
            error = 'Enter a header name.';
        } else if (name.length > MAX_HEADER_NAME_LENGTH) {
            error = `Header names can be at most ${MAX_HEADER_NAME_LENGTH} characters.`;
        } else if (!TOKEN_NAME.test(name)) {
            error = "Header names can only contain letters, digits and these characters: !#$%&'*+-.^_`|~";
        } else if (RESERVED_NAMES.includes(lower)) {
            error = `${name} is set by the system and cannot be overridden.`;
        } else if (seen.has(lower)) {
            error = 'Another header already uses this name.';
        } else if (value.length > MAX_HEADER_VALUE_LENGTH) {
            error = `Header values can be at most ${MAX_HEADER_VALUE_LENGTH} characters.`;
        } else if (r.secret && !value && !canKeepStoredSecret(r)) {
            error = r.hasValue
                // The server finds the stored secret by name, so a renamed one is unreachable.
                ? 'The name changed, so the saved value cannot be kept. Enter the value again.'
                : 'Enter a value for this secret header.';
        }
        if (name) seen.add(lower);
        return error;
    });
};

/** True when a blank secret value can lean on the stored one: it exists and the name still matches. */
const canKeepStoredSecret = (r: IHeaderRow): boolean =>
    !!r.hasValue && (r.name ?? '').trim().toLowerCase() === (r.originalName ?? '').trim().toLowerCase();

export interface IHeadersForTest {
    headers: Record<string, string>;
    /** A saved secret has no value in the browser, so it cannot be part of a test request. */
    omittedSavedSecret: boolean;
}

/**
 * The headers the browser can honestly send in a test request: non-secret headers, and secrets that
 * were freshly typed. Invalid rows and names the browser forbids are skipped. Tokens are not rendered.
 */
export const headersForTest = (rows: IHeaderRow[]): IHeadersForTest => {
    const errors = validateHeaderRows(rows);
    const headers: Record<string, string> = {};
    let omittedSavedSecret = false;
    rows.forEach((r, i) => {
        const name = (r.name ?? '').trim();
        if (!name) return;
        if (r.secret && !r.value) {
            if (r.hasValue) omittedSavedSecret = true;
            return;
        }
        if (errors[i]) return;
        const lower = name.toLowerCase();
        if (BROWSER_FORBIDDEN_NAMES.includes(lower) || lower.startsWith('sec-') || lower.startsWith('proxy-')) return;
        headers[name] = r.value ?? '';
    });
    return { headers, omittedSavedSecret };
};
